import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  RadioGroup,
  FormControlLabel,
  Radio
} from '@mui/material';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
  Save as SaveIcon,
  Business as BusinessIcon,
  Language as LanguageIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { authAPI, authUtils } from '../services/api';

const ProfilePage = () => {
  const { t, i18n } = useTranslation();
  const [userInfo, setUserInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // État pour la modification des informations personnelles
  const [profileData, setProfileData] = useState({
    prenom: '',
    nom: '',
    email: ''
  });
  const [profileLoading, setProfileLoading] = useState(false);
  
  // État pour le changement de mot de passe
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [passwordLoading, setPasswordLoading] = useState(false);

  useEffect(() => {
    loadUserInfo();
  }, []);

  const loadUserInfo = async () => {
    try {
      setLoading(true);
      
      // Essayer d'abord de charger depuis le localStorage
      const localUser = authUtils.getCurrentUser();
      if (localUser && localUser.prenom && localUser.nom && localUser.email) {
        setUserInfo(localUser);
        setProfileData({
          prenom: localUser.prenom || '',
          nom: localUser.nom || '',
          email: localUser.email || ''
        });
        setLoading(false);
        return;
      }
      
      // Sinon, charger depuis l'API
      const response = await authAPI.verifyToken();
      
      if (response.data.success) {
        const user = response.data.data.user;
        setUserInfo(user);
        // Initialiser les données du formulaire de profil
        setProfileData({
          prenom: user.prenom || '',
          nom: user.nom || '',
          email: user.email || ''
        });
      }
    } catch (error) {
      console.error('Erreur lors du chargement du profil:', error);
      setError(t('profilePage.errors.loadingError'));
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = (field, value) => {
    setPasswordData(prev => ({
      ...prev,
      [field]: value
    }));
    // Effacer les messages quand l'utilisateur tape
    if (error) setError('');
    if (success) setSuccess('');
  };

  const validatePasswordForm = () => {
    if (!passwordData.currentPassword) {
      setError(t('profilePage.errors.currentPasswordRequired'));
      return false;
    }
    if (passwordData.newPassword.length < 8) {
      setError(t('profilePage.errors.newPasswordMinLength'));
      return false;
    }
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError(t('profilePage.errors.passwordsDoNotMatch'));
      return false;
    }
    return true;
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (!validatePasswordForm()) return;
    
    setPasswordLoading(true);
    setError('');

    try {
      const response = await authAPI.changePassword(
        passwordData.currentPassword,
        passwordData.newPassword
      );
      
      if (response.data.success) {
        setSuccess(t('profilePage.success.passwordChanged'));
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
      }
    } catch (error) {
      console.error('Erreur lors du changement de mot de passe:', error);
      setError(
        error.response?.data?.message || 
        t('profilePage.errors.passwordChangeError')
      );
    } finally {
      setPasswordLoading(false);
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  // Fonctions pour la modification du profil
  const handleProfileChange = (field, value) => {
    setProfileData(prev => ({
      ...prev,
      [field]: value
    }));
    // Effacer les messages quand l'utilisateur tape
    if (error) setError('');
    if (success) setSuccess('');
  };

  const validateProfileForm = () => {
    if (!profileData.prenom.trim()) {
      setError(t('profilePage.errors.firstNameRequired'));
      return false;
    }
    if (!profileData.nom.trim()) {
      setError(t('profilePage.errors.lastNameRequired'));
      return false;
    }
    if (!profileData.email.trim()) {
      setError(t('profilePage.errors.emailRequired'));
      return false;
    }
    // Validation basique de l'email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(profileData.email)) {
      setError(t('profilePage.errors.emailInvalid'));
      return false;
    }
    return true;
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateProfileForm()) return;
    
    setProfileLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await authAPI.updateProfile(profileData);
      
      if (response.data.success) {
        setSuccess(t('profilePage.success.profileUpdated'));
        
        // Mettre à jour les informations utilisateur affichées
        const updatedUser = {
          ...userInfo,
          prenom: response.data.data.user.prenom,
          nom: response.data.data.user.nom,
          email: response.data.data.user.email
        };
        
        setUserInfo(updatedUser);
        
        // Mettre à jour le localStorage avec les nouvelles informations
        const token = authUtils.getToken();
        if (token) {
          authUtils.saveAuthInfo(token, updatedUser);
        }
        
        // Déclencher un événement pour notifier les autres composants
        window.dispatchEvent(new CustomEvent('userProfileUpdated', { 
          detail: { user: updatedUser } 
        }));
      }
      
    } catch (error) {
      console.error('Erreur lors de la mise à jour du profil:', error);
      setError(
        error.response?.data?.message || 
        t('profilePage.errors.profileUpdateError')
      );
    } finally {
      setProfileLoading(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Typography variant="h4" gutterBottom>
        <PersonIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        {t('profilePage.title')}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {userInfo && (
        <>
          {/* Informations personnelles */}
          <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom>
              <PersonIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              {t('profilePage.personalInfo')}
            </Typography>
            
            <Box component="form" onSubmit={handleProfileSubmit}>
              <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                <TextField
                  fullWidth
                  label={t('profilePage.firstName')}
                  value={profileData.prenom}
                  onChange={(e) => handleProfileChange('prenom', e.target.value)}
                  disabled={profileLoading}
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    }
                  }}
                />
                <TextField
                  fullWidth
                  label={t('profilePage.lastName')}
                  value={profileData.nom}
                  onChange={(e) => handleProfileChange('nom', e.target.value)}
                  disabled={profileLoading}
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    }
                  }}
                />
              </Box>

              <TextField
                fullWidth
                label={t('profilePage.email')}
                type="email"
                value={profileData.email}
                onChange={(e) => handleProfileChange('email', e.target.value)}
                disabled={profileLoading}
                required
                sx={{ 
                  mb: 3,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon />
                    </InputAdornment>
                  ),
                }}
              />

              {/* Informations non modifiables */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <Chip
                  label={userInfo.role === 'admin' ? t('profilePage.role.admin') : t('profilePage.role.member')}
                  color={userInfo.role === 'admin' ? 'primary' : 'default'}
                  size="small"
                />
                <BusinessIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  {userInfo.tenantNom}
                </Typography>
              </Box>

              <Button
                type="submit"
                variant="contained"
                disabled={profileLoading}
                startIcon={profileLoading ? <CircularProgress size={20} /> : <SaveIcon />}
                sx={{ borderRadius: 2 }}
              >
                {profileLoading ? t('profilePage.updating') : t('profilePage.updateProfile')}
              </Button>
            </Box>
          </Paper>

          {/* Langue */}
          <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom>
              <LanguageIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              {t('profilePage.language.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('profilePage.language.description')}
            </Typography>
            <RadioGroup
              value={i18n.language === 'en' || i18n.language.startsWith('en') ? 'en' : 'fr'}
              onChange={(e) => i18n.changeLanguage(e.target.value)}
              sx={{ mt: 1 }}
            >
              <FormControlLabel
                value="fr"
                control={<Radio />}
                label={t('profilePage.language.french')}
              />
              <FormControlLabel
                value="en"
                control={<Radio />}
                label={t('profilePage.language.english')}
              />
            </RadioGroup>
          </Paper>

          {/* Changement de mot de passe */}
          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom>
              <LockIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              {t('profilePage.changePassword')}
            </Typography>
            
            <Box component="form" onSubmit={handlePasswordSubmit}>
              <TextField
                margin="normal"
                required
                fullWidth
                name="currentPassword"
                label={t('profilePage.currentPassword')}
                type={showPasswords.current ? 'text' : 'password'}
                value={passwordData.currentPassword}
                onChange={(e) => handlePasswordChange('currentPassword', e.target.value)}
                disabled={passwordLoading}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={t('profilePage.togglePasswordVisibility')}
                        onClick={() => togglePasswordVisibility('current')}
                        edge="end"
                      >
                        {showPasswords.current ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                margin="normal"
                required
                fullWidth
                name="newPassword"
                label={t('profilePage.newPassword')}
                type={showPasswords.new ? 'text' : 'password'}
                value={passwordData.newPassword}
                onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
                disabled={passwordLoading}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={t('profilePage.togglePasswordVisibility')}
                        onClick={() => togglePasswordVisibility('new')}
                        edge="end"
                      >
                        {showPasswords.new ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                margin="normal"
                required
                fullWidth
                name="confirmPassword"
                label={t('profilePage.confirmPassword')}
                type={showPasswords.confirm ? 'text' : 'password'}
                value={passwordData.confirmPassword}
                onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
                disabled={passwordLoading}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={t('profilePage.togglePasswordVisibility')}
                        onClick={() => togglePasswordVisibility('confirm')}
                        edge="end"
                      >
                        {showPasswords.confirm ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Alert severity="info" sx={{ mt: 2, mb: 2, borderRadius: 2 }}>
                <Typography variant="body2">
                  {t('profilePage.passwordRequirements')}
                </Typography>
              </Alert>

              <Button
                type="submit"
                variant="contained"
                sx={{ mt: 2, borderRadius: 2 }}
                disabled={passwordLoading}
                startIcon={passwordLoading ? <CircularProgress size={20} /> : <SaveIcon />}
              >
                {passwordLoading ? t('profilePage.updatingPassword') : t('profilePage.updatePassword')}
              </Button>
            </Box>
          </Paper>
        </>
      )}
    </Container>
  );
};

export default ProfilePage;
